import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { checkout, API_URL } from '../utils/api';
import { ShieldCheck, Truck, RotateCcw, Lock, Tag, X, Check } from 'lucide-react';
import { SEO } from '../components/SEO';

const UK_EU_COUNTRIES = [
  'United Kingdom',
  'Republic of Ireland',
  'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
  'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
  'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands',
  'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
  'Norway', 'Switzerland',
];

const UK_COUNTIES = [
  '', 'Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire',
  'Cheshire', 'Cornwall', 'County Durham', 'Cumbria', 'Derbyshire', 'Devon',
  'Dorset', 'East Sussex', 'Essex', 'Gloucestershire', 'Greater London',
  'Greater Manchester', 'Hampshire', 'Herefordshire', 'Hertfordshire',
  'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire',
  'Merseyside', 'Norfolk', 'North Yorkshire', 'Northamptonshire',
  'Northumberland', 'Nottinghamshire', 'Oxfordshire', 'Rutland', 'Shropshire',
  'Somerset', 'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey',
  'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex',
  'West Yorkshire', 'Wiltshire', 'Worcestershire',
  'Antrim', 'Armagh', 'Down', 'Fermanagh', 'Londonderry', 'Tyrone',
  'Clackmannanshire', 'Edinburgh', 'Glasgow', 'Highland', 'Lothian',
  'Cardiff', 'Swansea', 'Newport', 'Powys', 'Gwynedd',
];

export const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, cartTotal } = useCart();
  const [loading, setLoading] = useState(false);

  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
  });
  const [billingDifferent, setBillingDifferent] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
  });
  const [orderNotes, setOrderNotes] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const updateShipping = (field, value) => setShippingAddress(prev => ({ ...prev, [field]: value }));
  const updateBilling = (field, value) => setBillingAddress(prev => ({ ...prev, [field]: value }));

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    try {
      const { discountCodes } = await import('../utils/api');
      const result = await discountCodes.apply(discountCode.trim(), cartTotal);
      setAppliedDiscount(result);
      toast.success(`Discount applied: -£${result.discount_amount.toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    }
    setApplyingDiscount(false);
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const finalTotal = appliedDiscount ? appliedDiscount.new_total : cartTotal;

  const validate = (isGuest) => {
    if (isGuest && !guestEmail) { toast.error('Please enter your email address'); return false; }
    if (!shippingAddress.name.trim()) { toast.error('Please enter the recipient\'s full name'); return false; }
    if (!shippingAddress.line1.trim()) { toast.error('Please enter the address line 1'); return false; }
    if (!shippingAddress.city.trim()) { toast.error('Please enter the city or town'); return false; }
    if (!shippingAddress.postcode.trim()) { toast.error('Please enter the postcode'); return false; }
    if (!shippingAddress.country) { toast.error('Please select a country'); return false; }
    if (billingDifferent) {
      if (!billingAddress.name.trim() || !billingAddress.line1.trim() || !billingAddress.city.trim() || !billingAddress.postcode.trim()) {
        toast.error('Please complete the billing address'); return false;
      }
    }
    return true;
  };

  const handleCheckout = async (isGuest = false) => {
    if (!validate(isGuest)) return;

    setLoading(true);
    try {
      const phone = isGuest ? guestPhone : shippingAddress.phone;
      const shipping = { ...shippingAddress, phone };
      const data = await checkout.createSession({
        items: cartItems,
        guest_email: isGuest ? guestEmail : null,
        shipping_address: shipping,
        billing_address: billingDifferent ? billingAddress : null,
        order_notes: orderNotes || null,
        origin_url: window.location.origin,
        discount_code: appliedDiscount ? appliedDiscount.code : null,
      });
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout failed', error);
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  const AddressForm = ({ prefix, address, onUpdate, showPhone = false, phone, onPhoneChange }) => (
    <div className="space-y-4">
      {/* Row 1: Full Name */}
      <div>
        <Label htmlFor={`${prefix}-name`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
          Full Name <span className="text-red-400">*</span>
        </Label>
        <Input
          id={`${prefix}-name`}
          value={address.name}
          onChange={(e) => onUpdate('name', e.target.value)}
          placeholder="e.g. James Smith"
          className="input-flushed mt-1"
          data-testid={`${prefix}-name-input`}
        />
      </div>

      {showPhone && (
        <div>
          <Label htmlFor={`${prefix}-phone`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
            Phone Number
          </Label>
          <Input
            id={`${prefix}-phone`}
            type="tel"
            value={phone || address.phone || ''}
            onChange={(e) => onPhoneChange ? onPhoneChange(e.target.value) : onUpdate('phone', e.target.value)}
            placeholder="e.g. 07700 900123"
            className="input-flushed mt-1"
            data-testid={`${prefix}-phone-input`}
          />
          <p className="text-[10px] text-[#666]/60 mt-1">For delivery updates only</p>
        </div>
      )}

      {/* Row 2: Address Line 1 */}
      <div>
        <Label htmlFor={`${prefix}-line1`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
          Address Line 1 <span className="text-red-400">*</span>
        </Label>
        <Input
          id={`${prefix}-line1`}
          value={address.line1}
          onChange={(e) => onUpdate('line1', e.target.value)}
          placeholder="House number and street name"
          className="input-flushed mt-1"
          data-testid={`${prefix}-line1-input`}
        />
      </div>

      {/* Row 3: Address Line 2 */}
      <div>
        <Label htmlFor={`${prefix}-line2`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
          Address Line 2 <span className="text-[#666]/40">(optional)</span>
        </Label>
        <Input
          id={`${prefix}-line2`}
          value={address.line2}
          onChange={(e) => onUpdate('line2', e.target.value)}
          placeholder="Flat, apartment, suite, building, floor"
          className="input-flushed mt-1"
          data-testid={`${prefix}-line2-input`}
        />
      </div>

      {/* Row 4: City + County */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${prefix}-city`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
            Town / City <span className="text-red-400">*</span>
          </Label>
          <Input
            id={`${prefix}-city`}
            value={address.city}
            onChange={(e) => onUpdate('city', e.target.value)}
            placeholder="e.g. London"
            className="input-flushed mt-1"
            data-testid={`${prefix}-city-input`}
          />
        </div>
        <div>
          <Label htmlFor={`${prefix}-county`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
            County / Region <span className="text-[#666]/40">(optional)</span>
          </Label>
          <Input
            id={`${prefix}-county`}
            value={address.county}
            onChange={(e) => onUpdate('county', e.target.value)}
            placeholder="e.g. Greater London"
            className="input-flushed mt-1"
            data-testid={`${prefix}-county-input`}
          />
        </div>
      </div>

      {/* Row 5: Postcode + Country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${prefix}-postcode`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
            Postcode <span className="text-red-400">*</span>
          </Label>
          <Input
            id={`${prefix}-postcode`}
            value={address.postcode}
            onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
            placeholder="e.g. SW1A 1AA"
            className="input-flushed mt-1 uppercase"
            data-testid={`${prefix}-postcode-input`}
          />
        </div>
        <div>
          <Label htmlFor={`${prefix}-country`} className="text-xs font-bold uppercase tracking-wider text-[#666]">
            Country <span className="text-red-400">*</span>
          </Label>
          <Select value={address.country} onValueChange={(v) => onUpdate('country', v)}>
            <SelectTrigger className="mt-1 h-11 border-[#E2E2E2] bg-transparent text-[#0A0A0A]" data-testid={`${prefix}-country-select`}>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {UK_EU_COUNTRIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const OrderSummary = ({ isGuest }) => (
    <div className="space-y-6">
      {/* Order Items */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#666] mb-3">Order Summary</h3>
        <div className="space-y-3">
          {cartItems.map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E2E2E2]/50 last:border-0">
              <div className="w-12 h-12 rounded-lg bg-[#EFE5D6]/30 overflow-hidden flex-shrink-0">
                <img
                  src={item.product?.images?.[0] ? `${API_URL}${item.product.images[0]}` : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=80'}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.product?.name}</p>
                <p className="text-[10px] text-[#666]">Size: {item.size} &middot; Qty: {item.quantity}</p>
              </div>
              <p className="text-sm font-bold text-[#0A0A0A] flex-shrink-0">&pound;{((item.product?.price || 0) * item.quantity).toFixed(2)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Discount Code */}
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-[#666]">
          Discount Code
        </Label>
        {appliedDiscount ? (
          <div className="mt-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="applied-discount">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-bold text-emerald-700">{appliedDiscount.code}</span>
              <span className="text-xs text-emerald-600 ml-2">-&pound;{appliedDiscount.discount_amount.toFixed(2)}</span>
            </div>
            <button onClick={removeDiscount} className="text-emerald-400 hover:text-red-500 transition-colors" data-testid="remove-discount-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="mt-1 flex gap-2">
            <Input
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="input-flushed flex-1"
              data-testid="discount-code-input"
            />
            <Button
              onClick={handleApplyDiscount}
              disabled={applyingDiscount || !discountCode.trim()}
              variant="outline"
              className="border-[#E2E2E2] text-[#666] hover:text-[#C9A84C] hover:border-[#C9A84C] text-xs uppercase tracking-wider font-bold px-4"
              data-testid="apply-discount-btn"
            >
              <Tag className="w-3 h-3 mr-1.5" />
              {applyingDiscount ? '...' : 'Apply'}
            </Button>
          </div>
        )}
      </div>

      {/* Order Notes */}
      <div>
        <Label htmlFor="order-notes" className="text-xs font-bold uppercase tracking-wider text-[#666]">
          Order Notes <span className="text-[#666]/40">(optional)</span>
        </Label>
        <textarea
          id="order-notes"
          value={orderNotes}
          onChange={(e) => setOrderNotes(e.target.value)}
          placeholder="Special delivery instructions, gift note, etc."
          className="w-full mt-1 px-3 py-2.5 text-sm border border-[#E2E2E2] rounded-lg bg-transparent text-[#0A0A0A] placeholder:text-[#666]/40 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] resize-none"
          rows={2}
          data-testid="order-notes-input"
        />
      </div>

      {/* Totals */}
      <div className="border-t border-[#E2E2E2] pt-4 space-y-2">
        <div className="flex justify-between text-sm text-[#666]">
          <span>Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} items)</span>
          <span>&pound;{cartTotal.toFixed(2)}</span>
        </div>
        {appliedDiscount && (
          <div className="flex justify-between text-sm text-emerald-600" data-testid="discount-line">
            <span>Discount ({appliedDiscount.code})</span>
            <span>-&pound;{appliedDiscount.discount_amount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm text-[#666]">
          <span>Shipping</span>
          <span className="text-emerald-600 font-medium">Free</span>
        </div>
        <div className="flex justify-between text-xs text-[#666]/80">
          <span>Estimated delivery</span>
          <span>{new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
        </div>
        <div className="flex justify-between text-xl font-bold pt-2 border-t border-[#E2E2E2]">
          <span>Total</span>
          <span data-testid={`${isGuest ? '' : 'reg-'}checkout-total`}>&pound;{finalTotal.toFixed(2)}</span>
        </div>
      </div>

      {/* Checkout Button */}
      <Button
        className="btn-primary w-full h-14 text-sm uppercase tracking-widest font-bold"
        onClick={() => handleCheckout(isGuest)}
        disabled={loading}
        data-testid={isGuest ? "guest-checkout-button" : "registered-checkout-button"}
      >
        <Lock className="w-4 h-4 mr-2" />
        {loading ? 'Processing...' : 'Proceed to Secure Payment'}
      </Button>

      {/* Policy Agreement */}
      <p className="text-[11px] text-[#666] text-center leading-relaxed" data-testid="checkout-policy-agreement">
        By completing your order, you agree to our{' '}
        <a href="/privacy-policy" className="text-[#C9A84C] hover:underline font-semibold">Privacy Policy</a>
        {' '}and{' '}
        <a href="/terms-conditions" className="text-[#C9A84C] hover:underline font-semibold">Terms & Conditions</a>.
      </p>

      {/* Trust Signals */}
      <div className="grid grid-cols-3 gap-2 pt-2">
        <div className="flex flex-col items-center text-center gap-1.5 py-3">
          <ShieldCheck className="w-5 h-5 text-[#C9A84C]" />
          <span className="text-[9px] uppercase tracking-wider text-[#666] font-bold">Secure<br />Checkout</span>
        </div>
        <div className="flex flex-col items-center text-center gap-1.5 py-3">
          <Truck className="w-5 h-5 text-[#C9A84C]" />
          <span className="text-[9px] uppercase tracking-wider text-[#666] font-bold">3-5 Day<br />Delivery</span>
        </div>
        <div className="flex flex-col items-center text-center gap-1.5 py-3">
          <RotateCcw className="w-5 h-5 text-[#C9A84C]" />
          <span className="text-[9px] uppercase tracking-wider text-[#666] font-bold">14-Day<br />Returns</span>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="border-t border-[#E2E2E2] pt-3 mt-1" data-testid="checkout-payment-methods">
        <p className="text-[9px] uppercase tracking-wider text-[#666] font-bold text-center mb-2">We Accept</p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="h-7 px-2.5 bg-white border border-[#E2E2E2] rounded text-[10px] font-bold flex items-center" style={{color: '#635BFF'}}>stripe</span>
          <span className="h-7 px-2.5 bg-white border border-[#E2E2E2] rounded text-[10px] font-black tracking-tight flex items-center" style={{color: '#1A1F71', fontStyle: 'italic'}}>VISA</span>
          <span className="h-7 px-2 bg-white border border-[#E2E2E2] rounded flex items-center gap-0"><span className="w-3.5 h-3.5 rounded-full" style={{backgroundColor:'#EB001B'}}></span><span className="w-3.5 h-3.5 rounded-full -ml-1.5" style={{backgroundColor:'#FF5F00'}}></span></span>
          <span className="h-7 px-2 bg-[#006FCF] rounded text-[8px] font-black text-white flex items-center tracking-tight">AMEX</span>
          <span className="h-7 px-2.5 bg-white border border-[#E2E2E2] rounded text-[10px] font-medium flex items-center text-black">Pay</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen section-padding" data-testid="checkout-page">
      <SEO title="Checkout" description="Complete your purchase at Vintage Wardrobe Co. Secure checkout powered by Stripe." path="/checkout" />
      <div className="container-custom max-w-5xl">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4 text-[#C9A84C]" />
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight" data-testid="checkout-title">Secure Checkout</h1>
        </div>
        <p className="text-xs text-[#666] mb-6">256-bit SSL encryption · Tracked delivery · 14-day free returns</p>

        <Tabs defaultValue={user ? "registered" : "guest"} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8" data-testid="checkout-tabs">
            <TabsTrigger value="guest" data-testid="guest-checkout-tab">Guest Checkout</TabsTrigger>
            <TabsTrigger value="registered" disabled={!user} data-testid="registered-checkout-tab">Registered User</TabsTrigger>
          </TabsList>

          {/* ═══ Guest Checkout ═══ */}
          <TabsContent value="guest" data-testid="guest-checkout-form">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left: Forms */}
              <div className="lg:col-span-3 space-y-8">
                {/* Contact */}
                <div className="space-y-4">
                  <h2 className="text-2xl font-heading font-semibold">Contact Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="guest-email" className="text-xs font-bold uppercase tracking-wider text-[#666]">
                        Email Address <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="guest-email"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="input-flushed mt-1"
                        data-testid="guest-email-input"
                      />
                      <p className="text-[10px] text-[#666]/60 mt-1">Order confirmation will be sent here</p>
                    </div>
                    <div>
                      <Label htmlFor="guest-phone" className="text-xs font-bold uppercase tracking-wider text-[#666]">
                        Phone Number
                      </Label>
                      <Input
                        id="guest-phone"
                        type="tel"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        placeholder="e.g. 07700 900123"
                        className="input-flushed mt-1"
                        data-testid="guest-phone-input"
                      />
                      <p className="text-[10px] text-[#666]/60 mt-1">For delivery updates only</p>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="space-y-4">
                  <h2 className="text-2xl font-heading font-semibold">Shipping Address</h2>
                  <AddressForm prefix="shipping" address={shippingAddress} onUpdate={updateShipping} />
                </div>

                {/* Billing Address Toggle */}
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group" data-testid="billing-different-toggle">
                    <input
                      type="checkbox"
                      checked={billingDifferent}
                      onChange={(e) => setBillingDifferent(e.target.checked)}
                      className="rounded border-[#E2E2E2] text-[#C9A84C] focus:ring-[#C9A84C] w-4 h-4"
                    />
                    <span className="text-sm text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors">
                      Billing address is different from shipping
                    </span>
                  </label>

                  {billingDifferent && (
                    <div className="pt-2">
                      <h2 className="text-2xl font-heading font-semibold mb-4">Billing Address</h2>
                      <AddressForm prefix="billing" address={billingAddress} onUpdate={updateBilling} />
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Summary */}
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-24 bg-[#F9F8F6] border border-[#E2E2E2] rounded-2xl p-6">
                  <OrderSummary isGuest={true} />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ═══ Registered Checkout ═══ */}
          <TabsContent value="registered" data-testid="registered-checkout-form">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left: Forms */}
              <div className="lg:col-span-3 space-y-8">
                {/* Shipping Address */}
                <div className="space-y-4">
                  <h2 className="text-2xl font-heading font-semibold">Shipping Address</h2>
                  <AddressForm
                    prefix="reg-shipping"
                    address={shippingAddress}
                    onUpdate={updateShipping}
                    showPhone={true}
                    phone={shippingAddress.phone}
                    onPhoneChange={(v) => updateShipping('phone', v)}
                  />
                </div>

                {/* Billing Address Toggle */}
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer group" data-testid="reg-billing-different-toggle">
                    <input
                      type="checkbox"
                      checked={billingDifferent}
                      onChange={(e) => setBillingDifferent(e.target.checked)}
                      className="rounded border-[#E2E2E2] text-[#C9A84C] focus:ring-[#C9A84C] w-4 h-4"
                    />
                    <span className="text-sm text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors">
                      Billing address is different from shipping
                    </span>
                  </label>

                  {billingDifferent && (
                    <div className="pt-2">
                      <h2 className="text-2xl font-heading font-semibold mb-4">Billing Address</h2>
                      <AddressForm prefix="reg-billing" address={billingAddress} onUpdate={updateBilling} />
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Summary */}
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-24 bg-[#F9F8F6] border border-[#E2E2E2] rounded-2xl p-6">
                  <OrderSummary isGuest={false} />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
